import { useTheme } from '@/src/context/ThemeContext';
import { getPublicTeamsUseCase } from '@/src/core/dependencies';
import { Team } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { ExploreTeamsScreen } from '@/src/presentation/screens/ExploreTeamsScreen';
import { logger } from '@/src/core/utils';
import { Stack, useRouter } from 'expo-router';
import { useEffect, useState } from 'react';

const PAGE_SIZE = 20;

export default function ExploreTeamsContainer() {
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();

  const [teams, setTeams] = useState<Team[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);

  useEffect(() => {
    loadTeams(0, true);
  }, []);

  const loadTeams = async (pageIndex: number, reset = false) => {
    if (reset) setLoading(true); else setLoadingMore(true);
    try {
      const offset = pageIndex * PAGE_SIZE;
      const result = await getPublicTeamsUseCase.execute(PAGE_SIZE, offset);
      if (reset) {
        setTeams(result);
      } else {
        setTeams((prev) => [...prev, ...result]);
      }
      setHasMore(result.length === PAGE_SIZE);
      setPage(pageIndex);
    } catch (error) {
      logger.error('ExploreTeams', 'Failed to load public teams', { error });
    } finally {
      if (reset) setLoading(false); else setLoadingMore(false);
    }
  };

  const handleEndReached = () => {
    if (!loadingMore && hasMore) {
      loadTeams(page + 1);
    }
  };

  return (
    <>
      <Stack.Screen options={{ title: t('navigation.exploreTeams') }} />
      <ExploreTeamsScreen
        teams={teams}
        searchQuery={searchQuery}
        loading={loading}
        loadingMore={loadingMore}
        onSearchChange={setSearchQuery}
        onTeamPress={(id) => router.push(`/team/${id}`)}
        onEndReached={handleEndReached}
        colors={colors}
      />
    </>
  );
}
